import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Plus, Trash2, Edit, Save, X, RefreshCw, ExternalLink, Copy,
  Eye, EyeOff, MapPin, Globe, FileText
} from 'lucide-react';
import ImageUploader from '@/components/ImageUploader';
import { adminFetch } from '../../utils/adminApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const blankPage = (slug = '', city = '') => ({
  slug,
  city_name: city,
  state: 'Bihar',
  is_published: true,
  meta_title: '',
  meta_description: '',
  meta_keywords: [],
  canonical_url: '',
  og_image: '',
  hero_badge_text: '',
  hero_title_prefix: 'RO Water Purifier Service in',
  hero_title_highlight: city,
  hero_subtitle: '',
  hero_image: '',
  hero_phone: '+919887068888',
  hero_trust_badges: [],
  stats: [],
  services_title_prefix: 'Our Services in',
  services_subtitle: '',
  services: [],
  form_title: '',
  form_subtitle: '',
  contact_phone: '+919887068888',
  contact_email: 'support@rodekho.com',
  contact_address: '',
  areas: [],
  why_choose_us: [],
  testimonials: [],
  faqs: [],
  cta_title: '',
  cta_subtitle: '',
  cta_phone: '9887068888',
  cta_whatsapp: '9887068888',
  latitude: '',
  longitude: '',
  seo_keywords: [],
  show_products_section: true,
  show_spareparts_section: true,
});

function AdminCityPages() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newPage, setNewPage] = useState({ slug: '', city_name: '' });

  const fetchPages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/city-pages`);
      if (res.ok) setPages(await res.json());
    } catch { toast.error('Failed to load city pages'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchPages(); }, [fetchPages]);

  const createPage = async () => {
    if (!newPage.slug || !newPage.city_name) {
      toast.error('Slug and City Name are required');
      return;
    }
    const slug = newPage.slug.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-');
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/city-pages`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(blankPage(slug, newPage.city_name))
      });
      if (res.ok) {
        toast.success('City page created!');
        setShowNew(false);
        setNewPage({ slug: '', city_name: '' });
        fetchPages();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create');
      }
    } catch { toast.error('Create failed'); }
  };

  const deletePage = async (slug) => {
    if (!confirm(`Delete city page "${slug}" permanently?`)) return;
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/city-pages/${slug}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Deleted'); fetchPages(); }
    } catch { toast.error('Delete failed'); }
  };

  const duplicatePage = async (sourceSlug) => {
    const slug = prompt('New slug (e.g. patna):');
    if (!slug) return;
    const city_name = prompt('New city name (e.g. Patna):');
    if (!city_name) return;
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/city-pages/${sourceSlug}/duplicate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: slug.toLowerCase().replace(/[^a-z0-9-]/g, '-'), city_name })
      });
      if (res.ok) { toast.success('Duplicated!'); fetchPages(); }
      else { const err = await res.json(); toast.error(err.detail || 'Duplicate failed'); }
    } catch { toast.error('Duplicate failed'); }
  };

  return (
    <div data-testid="admin-city-pages">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">City Landing Pages</h1>
          <p className="text-gray-500 text-sm">Create SEO-optimized landing pages for each city you serve</p>
        </div>
        <button onClick={() => setShowNew(true)} data-testid="add-city-page-btn"
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
          <Plus size={16} /> Add City Page
        </button>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl p-12 flex justify-center"><RefreshCw className="w-6 h-6 text-blue-500 animate-spin" /></div>
      ) : pages.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center" data-testid="city-pages-empty">
          <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-base font-medium text-gray-900 mb-1">No city pages yet</h3>
          <p className="text-gray-500 text-sm">Create your first city landing page to attract local customers</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pages.map(p => (
            <div key={p.slug} data-testid={`city-card-${p.slug}`} className="bg-white rounded-xl shadow-sm p-5 border-2 border-gray-100 hover:border-blue-200 transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-bold text-gray-900">{p.city_name}</h3>
                  <p className="text-xs text-gray-400 font-mono">/{p.slug}</p>
                </div>
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${p.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {p.is_published ? 'Published' : 'Draft'}
                </span>
              </div>
              <p className="text-xs text-gray-500 line-clamp-2 mb-3 min-h-[32px]">{p.meta_description || 'No description set'}</p>
              <div className="grid grid-cols-3 gap-2 text-center text-[11px] text-gray-500 mb-4 pb-3 border-b">
                <div><div className="font-bold text-gray-700">{p.areas?.length || 0}</div>Areas</div>
                <div><div className="font-bold text-gray-700">{p.services?.length || 0}</div>Services</div>
                <div><div className="font-bold text-gray-700">{p.faqs?.length || 0}</div>FAQs</div>
              </div>
              <div className="flex gap-1.5">
                <button onClick={() => setEditing(p)} data-testid={`edit-${p.slug}`}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs">
                  <Edit size={13} /> Edit
                </button>
                <a href={`/city/${p.slug}`} target="_blank" rel="noopener noreferrer" title="Preview"
                  className="px-2 py-1.5 text-gray-600 hover:bg-gray-100 rounded-lg border border-gray-200" data-testid={`preview-${p.slug}`}>
                  <ExternalLink size={13} />
                </a>
                <button onClick={() => duplicatePage(p.slug)} title="Duplicate" data-testid={`dup-${p.slug}`}
                  className="px-2 py-1.5 text-gray-600 hover:bg-gray-100 rounded-lg border border-gray-200">
                  <Copy size={13} />
                </button>
                <button onClick={() => deletePage(p.slug)} title="Delete" data-testid={`del-${p.slug}`}
                  className="px-2 py-1.5 text-red-500 hover:bg-red-50 rounded-lg border border-gray-200">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New City Modal */}
      {showNew && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="new-city-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">New City Landing Page</h3>
              <button onClick={() => setShowNew(false)} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL Slug *</label>
                <input type="text" value={newPage.slug} onChange={e => setNewPage({ ...newPage, slug: e.target.value })}
                  placeholder="e.g. patna (URL will be /city/patna)" className="w-full px-3 py-2 border border-gray-300 rounded-lg" data-testid="new-city-slug" />
                <p className="text-xs text-gray-400 mt-1">Lowercase letters, numbers, and hyphens only</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City Name *</label>
                <input type="text" value={newPage.city_name} onChange={e => setNewPage({ ...newPage, city_name: e.target.value })}
                  placeholder="e.g. Patna" className="w-full px-3 py-2 border border-gray-300 rounded-lg" data-testid="new-city-name" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowNew(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={createPage} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-testid="confirm-create-city">Create</button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <CityEditor page={editing} onClose={() => setEditing(null)} onSaved={() => { fetchPages(); setEditing(null); }} />
      )}
    </div>
  );
}

const inputCls = "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm";

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}
        {hint && <span className="text-xs text-gray-400 ml-1.5 font-normal">{hint}</span>}
      </label>
      {children}
    </div>
  );
}

function ListEditor({ label, items = [], onChange, columns, template, addLabel = '+ Add' }) {
  const list = Array.isArray(items) ? items : [];
  const addItem = () => onChange([...list, { ...template }]);
  const removeItem = (i) => { const n = [...list]; n.splice(i, 1); onChange(n); };
  const updateItem = (i, key, val) => { const n = [...list]; n[i] = { ...n[i], [key]: val }; onChange(n); };
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-gray-700">{label} <span className="text-xs text-gray-400">({list.length})</span></span>
        <button type="button" onClick={addItem} className="text-xs text-blue-600 hover:text-blue-800 font-medium">{addLabel}</button>
      </div>
      <div className="space-y-2">
        {list.map((item, i) => (
          <div key={i} className="bg-gray-50 border border-gray-200 rounded-lg p-3 relative">
            <button type="button" onClick={() => removeItem(i)} className="absolute top-2 right-2 p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded">
              <Trash2 size={13} />
            </button>
            <div className="space-y-2 pr-7">
              {columns.map(col => (
                col.type === 'textarea' ? (
                  <textarea key={col.key} value={item[col.key] || ''} onChange={e => updateItem(i, col.key, e.target.value)}
                    placeholder={col.placeholder} rows={col.rows || 2} className={inputCls + ' resize-y'} />
                ) : col.type === 'number' ? (
                  <input key={col.key} type="number" value={item[col.key] || ''} onChange={e => updateItem(i, col.key, parseFloat(e.target.value) || 0)}
                    placeholder={col.placeholder} className={inputCls} />
                ) : (
                  <input key={col.key} type="text" value={item[col.key] || ''} onChange={e => updateItem(i, col.key, e.target.value)}
                    placeholder={col.placeholder} className={inputCls} />
                )
              ))}
            </div>
          </div>
        ))}
        {list.length === 0 && <p className="text-xs text-gray-400 italic py-2">No items yet. Click &quot;{addLabel}&quot; to add.</p>}
      </div>
    </div>
  );
}

function StringListEditor({ label, items = [], onChange, placeholder, addLabel = '+ Add' }) {
  const list = Array.isArray(items) ? items : [];
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-gray-700">{label} <span className="text-xs text-gray-400">({list.length})</span></span>
        <button type="button" onClick={() => onChange([...list, ''])} className="text-xs text-blue-600 hover:text-blue-800 font-medium">{addLabel}</button>
      </div>
      <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
        {list.map((item, i) => (
          <div key={i} className="flex gap-2">
            <input type="text" value={item} onChange={e => { const n = [...list]; n[i] = e.target.value; onChange(n); }}
              placeholder={placeholder} className={inputCls} />
            <button type="button" onClick={() => { const n = [...list]; n.splice(i, 1); onChange(n); }}
              className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        {list.length === 0 && <p className="text-xs text-gray-400 italic py-2">No items yet.</p>}
      </div>
    </div>
  );
}

function CityEditor({ page, onClose, onSaved }) {
  const [data, setData] = useState({ ...page });
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('basic');
  const set = (k, v) => setData(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/city-pages/${page.slug}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
      });
      if (res.ok) { toast.success('Saved!'); onSaved(); }
      else toast.error('Save failed');
    } catch { toast.error('Save error'); }
    setSaving(false);
  };

  const TABS = [
    { id: 'basic', label: 'Basic & SEO' },
    { id: 'hero', label: 'Hero' },
    { id: 'services', label: 'Services & Stats' },
    { id: 'form', label: 'Booking Form' },
    { id: 'areas', label: 'Areas & Keywords' },
    { id: 'content', label: 'Why Us / Reviews / FAQ' },
    { id: 'cta', label: 'CTA' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto" data-testid="city-editor-modal">
      <div className="bg-white rounded-xl my-6 shadow-2xl max-w-5xl w-full flex flex-col" style={{ maxHeight: '94vh' }}>
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between rounded-t-xl z-10">
          <div className="flex items-center gap-2">
            <MapPin className="text-blue-600" size={18} />
            <span className="font-semibold text-gray-800">Edit: {data.city_name} <span className="text-xs text-gray-400 font-mono ml-2">/{data.slug}</span></span>
          </div>
          <div className="flex gap-2">
            <a href={`/city/${data.slug}`} target="_blank" rel="noopener noreferrer"
              className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm flex items-center gap-1.5" data-testid="editor-preview-btn">
              <ExternalLink size={13} /> Preview
            </a>
            <button onClick={onClose} className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm" data-testid="editor-cancel-btn">Cancel</button>
            <button onClick={save} disabled={saving} className="px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium flex items-center gap-1.5" data-testid="editor-save-btn">
              {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />} Save
            </button>
          </div>
        </div>

        <div className="border-b px-5 overflow-x-auto">
          <div className="flex gap-1">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                className={`px-4 py-2.5 text-sm whitespace-nowrap border-b-2 ${tab === t.id ? 'border-blue-600 text-blue-600 font-medium' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {tab === 'basic' && (
            <>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="City Name"><input type="text" value={data.city_name || ''} onChange={e => set('city_name', e.target.value)} className={inputCls} /></Field>
                <Field label="State"><input type="text" value={data.state || ''} onChange={e => set('state', e.target.value)} className={inputCls} /></Field>
                <Field label="Latitude" hint="for schema"><input type="text" value={data.latitude || ''} onChange={e => set('latitude', e.target.value)} className={inputCls} /></Field>
                <Field label="Longitude"><input type="text" value={data.longitude || ''} onChange={e => set('longitude', e.target.value)} className={inputCls} /></Field>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" checked={data.is_published !== false} onChange={e => set('is_published', e.target.checked)} className="w-4 h-4" data-testid="toggle-published" />
                  Published (visible on website)
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" checked={data.show_products_section !== false} onChange={e => set('show_products_section', e.target.checked)} className="w-4 h-4" />
                  Show Products Section
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" checked={data.show_spareparts_section !== false} onChange={e => set('show_spareparts_section', e.target.checked)} className="w-4 h-4" />
                  Show Spare Parts Section
                </label>
              </div>
              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><Globe size={15} /> SEO Settings</h3>
                <div className="space-y-3">
                  <Field label="Meta Title" hint={`${(data.meta_title || '').length}/60`}>
                    <input type="text" value={data.meta_title || ''} onChange={e => set('meta_title', e.target.value)} className={inputCls} data-testid="seo-title" />
                  </Field>
                  <Field label="Meta Description" hint={`${(data.meta_description || '').length}/160`}>
                    <textarea value={data.meta_description || ''} onChange={e => set('meta_description', e.target.value)} rows={3} className={inputCls + ' resize-y'} data-testid="seo-desc" />
                  </Field>
                  <Field label="Canonical URL"><input type="text" value={data.canonical_url || ''} onChange={e => set('canonical_url', e.target.value)} placeholder="https://www.rodekho.in/city/..." className={inputCls} /></Field>
                  <ImageUploader value={data.og_image || ''} onChange={url => set('og_image', url)} folder="cms" type="og" label="OG / Social Share Image" />
                </div>
              </div>
            </>
          )}

          {tab === 'hero' && (
            <>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Badge Text" hint="small pill above title"><input type="text" value={data.hero_badge_text || ''} onChange={e => set('hero_badge_text', e.target.value)} className={inputCls} data-testid="hero-badge" /></Field>
                <Field label="Hero Phone"><input type="text" value={data.hero_phone || ''} onChange={e => set('hero_phone', e.target.value)} className={inputCls} /></Field>
                <Field label="Title Prefix"><input type="text" value={data.hero_title_prefix || ''} onChange={e => set('hero_title_prefix', e.target.value)} className={inputCls} data-testid="hero-title-prefix" /></Field>
                <Field label="Title Highlight (city)"><input type="text" value={data.hero_title_highlight || ''} onChange={e => set('hero_title_highlight', e.target.value)} className={inputCls} data-testid="hero-title-highlight" /></Field>
              </div>
              <Field label="Hero Subtitle"><textarea value={data.hero_subtitle || ''} onChange={e => set('hero_subtitle', e.target.value)} rows={3} className={inputCls + ' resize-y'} data-testid="hero-subtitle" /></Field>
              <ImageUploader value={data.hero_image || ''} onChange={url => set('hero_image', url)} folder="cms" type="hero" label="Hero Background Image" />
              <StringListEditor label="Trust Badges" items={data.hero_trust_badges} onChange={v => set('hero_trust_badges', v)} placeholder="e.g. Free Doorstep Service" addLabel="+ Add Badge" />
            </>
          )}

          {tab === 'services' && (
            <>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Services Title Prefix"><input type="text" value={data.services_title_prefix || ''} onChange={e => set('services_title_prefix', e.target.value)} className={inputCls} /></Field>
                <Field label="Services Subtitle"><input type="text" value={data.services_subtitle || ''} onChange={e => set('services_subtitle', e.target.value)} className={inputCls} /></Field>
              </div>
              <ListEditor label="Services" items={data.services} onChange={v => set('services', v)}
                template={{ icon: 'Wrench', title: '', desc: '', price: '' }} addLabel="+ Add Service"
                columns={[
                  { key: 'icon', placeholder: 'Icon (Wrench, Shield, RefreshCw, Droplets...)' },
                  { key: 'title', placeholder: 'Title (e.g. RO Installation)' },
                  { key: 'desc', placeholder: 'Description', type: 'textarea' },
                  { key: 'price', placeholder: 'Price (e.g. From ₹350)' },
                ]} />
              <ListEditor label="Stats" items={data.stats} onChange={v => set('stats', v)}
                template={{ icon: 'Users', num: '', label: '' }} addLabel="+ Add Stat"
                columns={[
                  { key: 'icon', placeholder: 'Icon (Users, Wrench, Award, Clock...)' },
                  { key: 'num', placeholder: 'Number (e.g. 1000+)' },
                  { key: 'label', placeholder: 'Label (e.g. Happy Customers)' },
                ]} />
            </>
          )}

          {tab === 'form' && (
            <>
              <Field label="Form Title"><input type="text" value={data.form_title || ''} onChange={e => set('form_title', e.target.value)} className={inputCls} /></Field>
              <Field label="Form Subtitle"><textarea value={data.form_subtitle || ''} onChange={e => set('form_subtitle', e.target.value)} rows={2} className={inputCls + ' resize-y'} /></Field>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Contact Phone"><input type="text" value={data.contact_phone || ''} onChange={e => set('contact_phone', e.target.value)} className={inputCls} /></Field>
                <Field label="Contact Email"><input type="text" value={data.contact_email || ''} onChange={e => set('contact_email', e.target.value)} className={inputCls} /></Field>
              </div>
              <Field label="Contact Address"><textarea value={data.contact_address || ''} onChange={e => set('contact_address', e.target.value)} rows={2} className={inputCls + ' resize-y'} /></Field>
            </>
          )}

          {tab === 'areas' && (
            <>
              <StringListEditor label="Service Areas (shown as pills + form dropdown)" items={data.areas} onChange={v => set('areas', v)} placeholder="e.g. Chak Bhawani" addLabel="+ Add Area" />
              <StringListEditor label="SEO Keywords (hidden, for ranking)" items={data.seo_keywords} onChange={v => set('seo_keywords', v)} placeholder="e.g. RO Service in Darbhanga" addLabel="+ Add Keyword" />
            </>
          )}

          {tab === 'content' && (
            <>
              <ListEditor label="Why Choose Us" items={data.why_choose_us} onChange={v => set('why_choose_us', v)}
                template={{ icon: 'Star', title: '', desc: '' }} addLabel="+ Add Reason"
                columns={[
                  { key: 'icon', placeholder: 'Icon (Star, Shield, Award, Truck...)' },
                  { key: 'title', placeholder: 'Title' },
                  { key: 'desc', placeholder: 'Description', type: 'textarea' },
                ]} />
              <ListEditor label="Testimonials" items={data.testimonials} onChange={v => set('testimonials', v)}
                template={{ name: '', area: '', review: '', rating: 5 }} addLabel="+ Add Testimonial"
                columns={[
                  { key: 'name', placeholder: 'Customer name' },
                  { key: 'area', placeholder: 'Area' },
                  { key: 'review', placeholder: 'Review', type: 'textarea' },
                  { key: 'rating', placeholder: 'Rating 1-5', type: 'number' },
                ]} />
              <ListEditor label="FAQs" items={data.faqs} onChange={v => set('faqs', v)}
                template={{ q: '', a: '' }} addLabel="+ Add FAQ"
                columns={[
                  { key: 'q', placeholder: 'Question' },
                  { key: 'a', placeholder: 'Answer', type: 'textarea', rows: 3 },
                ]} />
            </>
          )}

          {tab === 'cta' && (
            <>
              <Field label="CTA Title"><input type="text" value={data.cta_title || ''} onChange={e => set('cta_title', e.target.value)} className={inputCls} /></Field>
              <Field label="CTA Subtitle"><textarea value={data.cta_subtitle || ''} onChange={e => set('cta_subtitle', e.target.value)} rows={2} className={inputCls + ' resize-y'} /></Field>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="CTA Phone" hint="without +91"><input type="text" value={data.cta_phone || ''} onChange={e => set('cta_phone', e.target.value)} className={inputCls} /></Field>
                <Field label="CTA WhatsApp" hint="without +91"><input type="text" value={data.cta_whatsapp || ''} onChange={e => set('cta_whatsapp', e.target.value)} className={inputCls} /></Field>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminCityPages;
